import { useState } from "react"
import { Button, Input } from "@aix/ui"
import { trpc } from "~/lib/trpc"
import toast from "react-hot-toast"
import EndpointTester from "./components/EndpointTester"

export default function DebugTab() {
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split("T")[0])

  // Queries
  const {
    data: allTrackables,
    refetch: refetchTrackables,
    isLoading: isLoadingTrackables,
  } = trpc.trackables.getAllTrackables.useQuery()

  const {
    data: entriesData,
    refetch: refetchEntries,
    isLoading: isLoadingEntries,
  } = trpc.trackables.getEntriesByDate.useQuery({
    date: selectedDate,
  })

  const {
    data: statsData,
    refetch: refetchStats,
    isLoading: isLoadingStats,
  } = trpc.trackables.getEntriesStats.useQuery({
    dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    dateTo: new Date().toISOString().split("T")[0],
  })

  const handleRefreshAll = () => {
    Promise.all([refetchTrackables(), refetchEntries(), refetchStats()])
      .then(() => toast.success("All data refreshed"))
      .catch(() => toast.error("Failed to refresh data"))
  }

  const handleCopyToClipboard = (data: any, label: string) => {
    void navigator.clipboard.writeText(JSON.stringify(data, null, 2))
    toast.success(`${label} copied to clipboard`)
  }

  return (
    <div className="p-6 space-y-6">
      {/* Section 1: Date Selector */}
      <div className="bg-secondary border-4 border-border p-6 shadow-lg">
        <h2 className="text-xl font-bold mb-4 text-primary">📅 Date Selector</h2>
        <div className="flex items-center gap-4">
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border-2 border-border w-64"
          />
          <Button
            onClick={() => setSelectedDate(new Date().toISOString().split("T")[0])}
            variant="outline"
            className="border-2 border-border"
          >
            Today
          </Button>
        </div>
      </div>

      {/* Section 2: Live Data Display */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* All Trackables */}
        <div className="bg-secondary border-4 border-border p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-primary">📋 All Trackables</h3>
            <Button
              onClick={() => handleCopyToClipboard(allTrackables, "Trackables")}
              variant="ghost"
              size="sm"
              disabled={!allTrackables}
            >
              📋 Copy
            </Button>
          </div>
          <div className="bg-background border-2 border-border p-4 rounded-none max-h-96 overflow-auto">
            {isLoadingTrackables ? (
              <div className="text-muted-foreground">Loading...</div>
            ) : (
              <pre className="text-xs">{JSON.stringify(allTrackables, null, 2)}</pre>
            )}
          </div>
        </div>

        {/* Entries for Selected Date */}
        <div className="bg-secondary border-4 border-border p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-primary">📊 Entries ({selectedDate})</h3>
            <Button
              onClick={() => handleCopyToClipboard(entriesData, "Entries")}
              variant="ghost"
              size="sm"
              disabled={!entriesData}
            >
              📋 Copy
            </Button>
          </div>
          <div className="bg-background border-2 border-border p-4 rounded-none max-h-96 overflow-auto">
            {isLoadingEntries ? (
              <div className="text-muted-foreground">Loading...</div>
            ) : entriesData && entriesData.entries && entriesData.entries.length > 0 ? (
              <pre className="text-xs">{JSON.stringify(entriesData, null, 2)}</pre>
            ) : (
              <div className="text-muted-foreground">No entries for this date</div>
            )}
          </div>
        </div>

        {/* Stats Summary */}
        <div className="bg-secondary border-4 border-border p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-primary">📈 Stats Summary</h3>
            <Button
              onClick={() => handleCopyToClipboard(statsData, "Stats")}
              variant="ghost"
              size="sm"
              disabled={!statsData}
            >
              📋 Copy
            </Button>
          </div>
          <div className="bg-background border-2 border-border p-4 rounded-none space-y-2">
            {isLoadingStats ? (
              <div className="text-muted-foreground">Loading...</div>
            ) : statsData ? (
              <>
                <div className="flex justify-between text-sm">
                  <span className="font-semibold">Total Entries:</span>
                  <span>{statsData.totalEntries || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="font-semibold">Avg Completion:</span>
                  <span>{statsData.averageCompletion?.toFixed(1) || 0}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="font-semibold">Date Range:</span>
                  <span className="text-xs">
                    {statsData.dateRange?.from} to {statsData.dateRange?.to}
                  </span>
                </div>
                <div className="mt-4">
                  <div className="text-xs font-semibold mb-2">Trackables Count:</div>
                  <div className="text-xs">
                    <div>Active: {allTrackables?.filter((t: any) => t.isActive).length || 0}</div>
                    <div>Inactive: {allTrackables?.filter((t: any) => !t.isActive).length || 0}</div>
                    <div>Morning: {allTrackables?.filter((t: any) => t.checkInWindow === "morning").length || 0}</div>
                    <div>Evening: {allTrackables?.filter((t: any) => t.checkInWindow === "evening").length || 0}</div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-muted-foreground">No stats available</div>
            )}
          </div>
        </div>
      </div>

      {/* Section 3: Quick Actions */}
      <div className="bg-secondary border-4 border-border p-6 shadow-lg">
        <h2 className="text-xl font-bold mb-4 text-primary">⚡ Quick Actions</h2>
        <div className="flex flex-wrap gap-4">
          <Button onClick={handleRefreshAll} variant="outline" className="border-2 border-border">
            🔄 Refresh All Data
          </Button>
        </div>
      </div>

      {/* Section 5: API Endpoint Tester */}
      <div className="bg-secondary border-4 border-border p-6 shadow-lg">
        <h2 className="text-xl font-bold mb-4 text-primary">🔧 API Endpoint Tester</h2>
        <EndpointTester />
      </div>
    </div>
  )
}
